/**
 * Which destination is showing, and which full-viewport surface is over it.
 *
 * Selection is React state and **never** derived from the URL. F-NAV-23 is
 * explicit that the product has no deep links and no restoration: a cold start
 * always lands on Train, whatever address bar it was opened from. The URL is
 * written *from* this state (§1.4 "writing a URL that reflects state") and is
 * never read back into it, because the moment it were read back, a refresh on
 * `/journal` would resurrect exactly the restoration the spec forbids.
 */

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { DestinationId } from "../shell/destinations";
import { LAUNCH_DESTINATION } from "../shell/destinations";
import type { BuildTool } from "../shell/sidebarModel";

export interface NavigationState {
  readonly destination: DestinationId;
  /**
   * F-NAV-04. Where the user was before they entered Journal, so a "back to
   * where I was" affordance has an origin. Re-selecting Journal while already
   * in Journal must not overwrite it — otherwise the origin becomes Journal
   * itself and the affordance points at the screen you are already on.
   */
  readonly destinationBeforeJournal: DestinationId;
  readonly select: (destination: DestinationId) => void;
  /**
   * Which tool the Build destination is showing. It lives in shell state rather
   * than inside the Build feature because two shell rules depend on it: a Build
   * sidebar row is selected only when destination **and** tool match (F-NAV-07),
   * and the Build shortcut forces the tool back to Workouts (F-NAV-13).
   */
  readonly buildTool: BuildTool;
  readonly selectBuildTool: (tool: BuildTool) => void;
  /** The full-viewport surface currently covering the shell, if any. */
  readonly fullViewportPath: string | null;
  readonly presentFullViewport: (path: string) => void;
  readonly dismissFullViewport: () => void;
}

const NavigationContext = createContext<NavigationState | null>(null);

export function useNavigation(): NavigationState {
  const navigation = useContext(NavigationContext);
  if (navigation === null) {
    throw new Error(
      "useNavigation() requires an enclosing <NavigationProvider>, placed by <AppStateProvider>.",
    );
  }
  return navigation;
}

export interface NavigationProviderProps {
  /**
   * Only a test names a starting destination. The app never passes one: every
   * cold start is Train (F-NAV-23), and an override reachable from production
   * code is how a "remember my last tab" feature gets added by accident.
   */
  readonly initialDestination?: DestinationId;
  readonly children?: ReactNode;
}

export function NavigationProvider({
  initialDestination,
  children,
}: NavigationProviderProps): ReactNode {
  // One state object rather than two: the journal-origin rule reads the
  // *previous* destination at the moment of the change, and two independent
  // setters would have to read one another's pre-commit value to do that.
  const [selection, setSelection] = useState<{
    readonly destination: DestinationId;
    readonly destinationBeforeJournal: DestinationId;
  }>(() => ({
    destination: initialDestination ?? LAUNCH_DESTINATION,
    destinationBeforeJournal: LAUNCH_DESTINATION,
  }));
  const [buildTool, setBuildTool] = useState<BuildTool>("workouts");
  const [fullViewportPath, setFullViewportPath] = useState<string | null>(null);

  const select = useCallback((next: DestinationId) => {
    setSelection((current) => {
      if (next === current.destination) return current;
      const entersJournal = next === "journal" && current.destination !== "journal";
      return {
        destination: next,
        destinationBeforeJournal: entersJournal
          ? current.destination
          : current.destinationBeforeJournal,
      };
    });
  }, []);

  const selectBuildTool = useCallback((tool: BuildTool) => {
    setBuildTool(tool);
  }, []);

  const presentFullViewport = useCallback((path: string) => {
    setFullViewportPath(path);
  }, []);

  const dismissFullViewport = useCallback(() => {
    setFullViewportPath(null);
  }, []);

  const value = useMemo<NavigationState>(
    () => ({
      destination: selection.destination,
      destinationBeforeJournal: selection.destinationBeforeJournal,
      select,
      buildTool,
      selectBuildTool,
      fullViewportPath,
      presentFullViewport,
      dismissFullViewport,
    }),
    [
      selection,
      select,
      buildTool,
      selectBuildTool,
      fullViewportPath,
      presentFullViewport,
      dismissFullViewport,
    ],
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}
